import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMemberIds } from '../../hooks/useMemberIds'
import { Icon } from '../../components/Icon'
import { errorMessage } from '../../api/client'
import { useEphemeral } from '../../state/ephemeral'
import { isBotUser } from '../../lib/bots'
import { toast } from '../../state/toasts'
import { ensureMessageLoaded, loadOlderMessages } from '../../sync/history'
import type { TimelineMessage } from '../../sync/outboxMerge'
import { useConversation } from './ConversationContext'
import { MessageItem } from './MessageItem'
import { localAnswers } from './questions'
import { receiptFor } from './receipts'
import { buildTimeline } from './timeline'
import { useChatScroll } from './useChatScroll'
import { useMarkRead } from './useReadTracking'

interface Props {
  timeline: TimelineMessage[]
  jump: { id: string; seq?: number; nonce: number } | null
  onJumpHandled: () => void
}

const EMPTY_READS: Record<string, number> = {}

export function MessageList({ timeline, jump, onJumpHandled }: Props) {
  const { channel, me, users } = useConversation()
  const [newAfterSeq] = useState<number | null>(() => (channel.isMember && channel.unread > 0 ? channel.lastReadSeq : null))
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const reads = useEphemeral((s) => s.reads[channel.id] ?? EMPTY_READS)

  const entries = useMemo(() => buildTimeline(timeline, { meId: me.id, newAfterSeq }), [timeline, me.id, newAfterSeq])
  // Which option this device answered each question with, so its buttons lock as soon as it is tapped.
  const answers = useMemo(() => localAnswers(timeline), [timeline])
  // Who the ticks are about: the other members, without the bots, which never read anything.
  const memberIds = useMemberIds(channel)
  const audience = useMemo(
    () => memberIds.filter((id) => id !== me.id && !isBotUser(users.get(id))),
    [memberIds, me.id, users],
  )

  const loadOlder = useCallback(async () => {
    if (!channel.hasMore) return
    setLoadingOlder(true)
    try {
      await loadOlderMessages(channel.id)
    } catch (error) {
      toast(`Couldn't load older messages. ${errorMessage(error)}`, { tone: 'error' })
    } finally {
      setLoadingOlder(false)
    }
  }, [channel.id, channel.hasMore])

  const first = entries.find((e) => e.kind === 'message')
  const last = entries[entries.length - 1]
  const scroll = useChatScroll({ firstKey: first?.key, lastKey: last?.key, count: entries.length, hasMore: channel.hasMore, onReachTop: loadOlder })
  const { containerRef, markInitialised, initialised, scrollToBottom } = scroll

  useMarkRead(channel, scroll.atBottom)

  // First render with data: go to the jump target, the "New messages" divider, or the bottom.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el || initialised.current || entries.length === 0) return
    markInitialised()
    if (jump) return
    const divider = el.querySelector<HTMLElement>('[data-divider="new"]')
    if (divider) {
      el.scrollTop = Math.max(0, divider.offsetTop - 24)
      scroll.atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    } else {
      el.scrollTop = el.scrollHeight
    }
  }, [entries.length, jump, containerRef, initialised, markInitialised, scroll.atBottomRef])

  // Load and scroll to a message (search result, reply quote, pinned item).
  const jumpRef = useRef(jump)
  useEffect(() => {
    jumpRef.current = jump
  }, [jump])
  useEffect(() => {
    if (!jump) return
    let cancelled = false
    scroll.atBottomRef.current = false
    ;(async () => {
      const found = await ensureMessageLoaded(channel.id, jump.id, jump.seq).catch(() => false)
      if (cancelled) return
      if (!found) {
        toast("That message isn't available any more")
        onJumpHandled()
        return
      }
      // The live query renders the loaded page asynchronously: wait (up to ~3 s) for the element.
      const deadline = Date.now() + 3000
      const tryScroll = () => {
        if (cancelled) return
        const el = containerRef.current?.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(jump.id)}"]`)
        if (!el && Date.now() < deadline) {
          requestAnimationFrame(tryScroll)
          return
        }
        if (el) {
          scroll.atBottomRef.current = false
          el.scrollIntoView({ block: 'center' })
          el.focus({ preventScroll: true })
          setHighlightId(jump.id)
          setTimeout(() => setHighlightId((h) => (h === jump.id ? null : h)), 2200)
        }
        if (jumpRef.current?.nonce === jump.nonce) onJumpHandled()
      }
      requestAnimationFrame(tryScroll)
    })()
    return () => {
      cancelled = true
    }
  }, [jump, channel.id, containerRef, onJumpHandled, scroll.atBottomRef])

  // The last confirmed message of mine gets "Seen by".
  const lastOwnId = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const { message, pending } = timeline[i]
      if (!pending && message.authorId === me.id && !message.deletedAt) return message.id
    }
    return null
  }, [timeline, me.id])

  return (
    // The button below is placed against this pane, which stops above the message box rather than behind it.
    <div className="history-pane">
      <div
        className="chat-history"
        ref={containerRef}
        onScroll={scroll.onScroll}
        tabIndex={0}
        aria-label="Message history"
        role="log"
      >
        <div ref={scroll.contentRef}>
          {channel.hasMore ? (
            <div className="history-status">
              {loadingOlder ? (
                <>
                  <span className="spinner" /> Loading older messages…
                </>
              ) : (
                <button type="button" className="text-button" onClick={() => void loadOlder()}>
                  Load older messages
                </button>
              )}
            </div>
          ) : null}
          {entries.length === 0 ? (
            <div className="start-chat">
              <h3>No messages yet</h3>
              <p>{channel.isMember ? 'Send a message below to get things started.' : 'Nobody has posted here yet.'}</p>
            </div>
          ) : (
            entries.map((entry) => {
              if (entry.kind === 'day') {
                return (
                  <div key={entry.key} className="date-divider" role="separator">
                    {entry.label}
                  </div>
                )
              }
              if (entry.kind === 'new') {
                return (
                  <div key={entry.key} className="date-divider new-divider" role="separator" data-divider="new">
                    New messages
                  </div>
                )
              }
              const { message } = entry.item
              const seenBy =
                message.id === lastOwnId
                  ? Object.entries(reads)
                      // Bots never read: their read position only moves when they post.
                      .filter(([userId, seq]) => userId !== me.id && seq >= message.seq && !isBotUser(users.get(userId)))
                      .map(([userId]) => userId)
                  : null
              return (
                <MessageItem
                  key={entry.key}
                  item={entry.item}
                  continued={entry.continued}
                  answersAbove={entry.answersAbove}
                  highlighted={highlightId === message.id}
                  seenBy={seenBy}
                  receipt={receiptFor({
                    mine: message.authorId === me.id,
                    pending: entry.item.pending ? (entry.item.pending.state === 'failed' ? 'failed' : 'sending') : null,
                    seq: message.seq,
                    reads,
                    audience,
                  })}
                  users={users}
                  answeredWith={answers.get(message.id) ?? null}
                  doneHere={answers.has(message.id)}
                />
              )
            })
          )}
        </div>
      </div>
      {!scroll.atBottom && entries.length > 0 ? (
        <button type="button" className="button jump-latest" onClick={scrollToBottom}>
          <Icon name="arrowDown" />
          Jump to latest
        </button>
      ) : null}
    </div>
  )
}
